import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorBoundary } from './ErrorBoundary';

interface GlobalErrorBoundaryProps {
  children: ReactNode;
  enableReporting?: boolean;
  reportingEndpoint?: string;
  onError?: (error: Error, errorInfo: ErrorInfo, context: 'global') => void;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  isReporting: boolean;
  reportSubmitted: boolean;
}

/**
 * Global Error Boundary that provides application-wide error handling
 * with enhanced reporting and recovery options
 */
export class GlobalErrorBoundary extends Component<GlobalErrorBoundaryProps, GlobalErrorBoundaryState> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      isReporting: false,
      reportSubmitted: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<GlobalErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: ErrorBoundary.generateErrorId()
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Log to global error handler
    this.handleGlobalError(error, errorInfo);

    // Call custom error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo, 'global');
    }
  }

  private handleGlobalError = async (error: Error, errorInfo: ErrorInfo): Promise<void> => {
    const errorData = {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      memory: (performance as any).memory ? {
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize
      } : null,
      localStorage: {
        available: this.isLocalStorageAvailable(),
        size: this.getLocalStorageSize()
      }
    };

    // Log to console
    console.error('Global Error Boundary caught error:', errorData);

    // Store in session storage for debugging
    try {
      sessionStorage.setItem('jiraz_last_error', JSON.stringify(errorData));
    } catch (e) {
      console.warn('Failed to store error in sessionStorage:', e);
    }

    // Report to monitoring service if enabled
    if (this.props.enableReporting && this.props.reportingEndpoint) {
      this.reportError(errorData);
    }
  };

  private isLocalStorageAvailable = (): boolean => {
    try {
      const test = '__test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  };

  private getLocalStorageSize = (): number => {
    try {
      let total = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          total += localStorage[key].length + key.length;
        }
      }
      return total;
    } catch {
      return 0;
    }
  };

  private reportError = async (errorData: any): Promise<void> => {
    if (this.state.reportSubmitted) return;

    this.setState({ isReporting: true });

    try {
      // Simulate error reporting (in production, this would send to your error service)
      console.log('Reporting error to monitoring service:', errorData);

      // Example: Send to Sentry, Bugsnag, or custom endpoint
      // const response = await fetch(this.props.reportingEndpoint!, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData)
      // });

      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      this.setState({
        isReporting: false,
        reportSubmitted: true
      });
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
      this.setState({ isReporting: false });
    }
  };

  private handleReload = (): void => {
    // Clear any potentially corrupted state
    try {
      sessionStorage.removeItem('jiraz_app_state');
    } catch (e) {
      console.warn('Failed to clear session state:', e);
    }

    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.href = window.location.origin;
  };

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      isReporting: false,
      reportSubmitted: false
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="min-h-screen px-4 text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true" />

            {/* Center the error modal */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

            <div className="inline-block w-full max-w-2xl p-8 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              {/* Error icon */}
              <div className="flex justify-center mb-6">
                <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>

              {/* Error message */}
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Application Error
                </h1>
                <p className="text-gray-600">
                  We apologize, but the application encountered an unexpected error.
                  Our team has been notified and is working to resolve this issue.
                </p>
              </div>

              {/* Error details section */}
              <details className="mb-6 p-4 bg-gray-50 rounded-lg">
                <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                  Technical Details
                </summary>
                <div className="mt-3 space-y-2 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Error ID:</span> {this.state.errorId}
                  </div>
                  {this.state.error && (
                    <div>
                      <span className="font-medium">Error:</span> {this.state.error.message}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Time:</span> {new Date().toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">URL:</span> {window.location.href}
                  </div>
                </div>
              </details>

              {/* Reporting status */}
              {this.props.enableReporting && (
                <div className="mb-6 p-3 bg-blue-50 rounded-lg">
                  {this.state.isReporting ? (
                    <div className="flex items-center text-sm text-blue-700">
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Reporting error to support team...
                    </div>
                  ) : this.state.reportSubmitted ? (
                    <div className="flex items-center text-sm text-green-700">
                      <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Error reported successfully. Reference ID: {this.state.errorId}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Action buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Reload Application
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Go to Homepage
                </button>

                <button
                  onClick={this.handleReset}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Try to Continue
                </button>
              </div>

              {/* Additional help */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="text-center text-sm text-gray-600">
                  <p className="mb-2">If the problem persists:</p>
                  <div className="space-y-1">
                    <p>• Clear your browser cache and cookies</p>
                    <p>• Try using a different browser</p>
                    <p>• Contact support with Error ID: <span className="font-mono bg-gray-100 px-1 rounded">{this.state.errorId}</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
